import { useCallback, useMemo } from 'react'

import { Select, Tag } from 'antd'
import { isArray, isFunction } from 'lodash'

import { isNonEmptyArray, isObject } from '../../../utils'
import '../../../assets/entities-selector.scss'

const EntitiesSelector = ({
  className = '', disabled, empty = false, entityOptions, highlightValues = [], loading, multiple = 'multiple', notFoundContent,
  placeholder, showSearch = false, style = {}, tagPlaceholder = true, values = [], onChange, onSearch, dropdownRender, renderOption,
  selectorClassName = ''
}) => {
  const highlightMap = useMemo(() => {
    const map = {}

    if (isNonEmptyArray(highlightValues)) {
      highlightValues.forEach(value => {
        map[value] = true
      })
    }

    return map
  }, [highlightValues])

  const sortedOptions = useMemo(() => {
    let sortedOptions = entityOptions

    if (Object.keys(highlightMap).length > 0) {
      sortedOptions = [...sortedOptions].sort((a, b) => {
        const aWeight = highlightMap[a.value] ? 1 : 0
        const bWeight = highlightMap[b.value] ? 1 : 0

        return bWeight - aWeight
      })
    }

    return sortedOptions
  }, [highlightMap, entityOptions])

  const handleFilter = useCallback((input, option) => {
    if (isFunction(onSearch)) {
      return true
    } else {
      const { label, title } = option.props

      return (
        (label && label.toLowerCase().includes(input.toLowerCase())) ||
        (title && title.toLowerCase().includes(input.toLowerCase()))
      )
    }
  }, [onSearch])

  const handleOptionChange = useCallback((value, option) => {
    let entities = null

    if (isArray(option)) {
      entities = option.map(o => o.props.entity)
    } else if (isObject(option?.props)) {
      entities = option.props.entity
    }

    onChange(value, option, entities)
  }, [onChange])

  /** The tag is the historical look : `tagPlaceholder={false}` falls back on the plain antd grey text. */
  const Placeholder = useMemo(() => {
    if (isNonEmptyArray(values) || !placeholder) {
      return false
    }

    return tagPlaceholder ? <Tag className='fs-15 h-20'><span>{placeholder}</span></Tag> : placeholder
  }, [values, placeholder, tagPlaceholder])

  return (
    <Select
      className={selectorClassName}
      placeholder={Placeholder}
      disabled={disabled}
      loading={loading}
      mode={multiple}
      allowClear
      showSearch={showSearch}
      value={values}
      optionFilterProp='label'
      notFoundContent={notFoundContent}
      onChange={handleOptionChange}
      style={{ ...style }}
      dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
      onSearch={onSearch}
      filterOption={handleFilter}
      dropdownRender={isFunction(dropdownRender) ? dropdownRender : undefined}
    >
      {empty && (
        <Select.Option
          title='/'
          value={null}
          label='/'
          entity={null}
        >
              /
        </Select.Option>
      )}
      {sortedOptions.map(entityOption => (
        <Select.Option
          key={'entity_' + entityOption.value}
          className={className + (highlightMap[entityOption.value] ? ' color-neutral' : '')}
          title={entityOption.tooltip}
          value={entityOption.value}
          label={entityOption.label}
          disabled={entityOption.disabled}
          entity={entityOption.entity}
        >
          {isFunction(renderOption) ? renderOption(entityOption.entity) : entityOption.label}
        </Select.Option>
      ))}
    </Select>
  )
}

export default EntitiesSelector
